<!--
📌 Componente: ZeiterfassungView.vue
📂 Caminho: src/views/ZeiterfassungView.vue
📝 Descrição: Relógio de Ponto. Permite registrar Entrada (In) e Saída (Out)
    usando Scanner de QR Code ou Inserção Manual.
-->

<template>
  <div
    class="zeit-root fixed inset-0 bg-azul-profundo flex flex-col font-sans overflow-hidden text-texto-principal"
  >
    <!-- HEADER: no fluxo flex, nunca scrolla -->
    <header
      class="bg-azul-profundo text-branco flex flex-col items-center justify-center pt-[env(safe-area-inset-top)] pb-2 px-6 shrink-0 text-center"
    >
      <!-- A. Badge Network Status -->
      <div
        v-if="!isOnline"
        class="absolute top-2 right-2 flex items-center bg-vermelho-suave text-branco text-xs font-bold px-2 py-1 rounded-full shadow"
      >
        <span class="w-2 h-2 rounded-full bg-branco animate-pulse mr-1"></span>
        Offline
      </div>

      <div
        class="text-4xl font-mono font-bold leading-none tracking-tight mb-1"
        aria-live="polite"
      >
        {{ currentTime }}
      </div>
      <div class="text-sm text-borda-suave capitalize font-medium">
        {{ currentDateStr }}
      </div>
    </header>

    <!-- ÁREA PRINCIPAL: Botões preenchem o espaço restante, sem scroll -->
    <main
      v-if="state === 'IDLE'"
      class="zeit-main flex-1 flex flex-col w-full overflow-hidden min-h-0 pb-[env(safe-area-inset-bottom)]"
    >
      <!-- Entrada (Einstempeln) -->
      <button
        @click="openScanner('IN')"
        class="zeit-btn flex-1 bg-verde-profundo active:bg-verde-profundo/80 transition-colors flex flex-col items-center justify-center gap-1 p-2 group zeit-divider"
        :disabled="!isOnline || isProcessing"
        :class="{ 'opacity-75 cursor-not-allowed': !isOnline }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="zeit-icon text-branco"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"
          />
        </svg>
        <span
          class="zeit-label font-black text-branco tracking-widest uppercase drop-shadow-md"
        >
          {{ $t("zeiterfassung.in") || "Einstempeln" }}
        </span>
      </button>

      <!-- Saída (Ausstempeln) -->
      <button
        @click="openScanner('OUT')"
        class="zeit-btn flex-1 bg-amarelo-profundo active:bg-amarelo-profundo/80 transition-colors flex flex-col items-center justify-center gap-1 p-2 group"
        :disabled="!isOnline || isProcessing"
        :class="{ 'opacity-75 cursor-not-allowed': !isOnline }"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="zeit-icon text-branco"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
          />
        </svg>
        <span
          class="zeit-label font-black text-branco tracking-widest uppercase drop-shadow-md"
        >
          {{ $t("zeiterfassung.out") || "Ausstempeln" }}
        </span>
      </button>
    </main>

    <!-- ESTADO DE SCANNER -->
    <div
      v-else-if="state === 'SCANNING'"
      class="flex-1 flex w-full h-full bg-cinza-900 relative"
    >
      <!-- Botão de Voltar/Cancelar absoluto sobre a câmera -->
      <button
        @click="cancelScanner"
        class="absolute top-6 left-6 z-50 bg-cinza-900/50 hover:bg-cinza-900/70 text-branco rounded-full p-3 backdrop-blur-sm transition-colors border border-branco/20 shadow-lg"
        aria-label="Cancelar"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="h-8 w-8"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M10 19l-7-7m0 0l7-7m-7 7h18"
          />
        </svg>
      </button>

      <div class="absolute top-6 right-6 z-50 pointer-events-none">
        <span
          class="bg-cinza-900/60 px-4 py-2 rounded-full text-branco font-bold backdrop-blur-sm text-xl uppercase shadow-lg border border-branco/20"
        >
          {{
            currentAction === "IN"
              ? $t("zeiterfassung.in") || "Einstempeln"
              : $t("zeiterfassung.out") || "Ausstempeln"
          }}
        </span>
      </div>

      <!-- Container flex fixo para câmera -->
      <div class="w-full h-full flex flex-col items-center justify-center">
        <div
          class="flex-1 w-full max-w-4xl max-h-full flex items-center justify-center p-4"
        >
          <!-- Customização sutil do beep para in/out no scanner via props dinâmicas (frequências) -->
          <QRCodeLeserScann
            ref="qrScanner"
            :active="true"
            :pauseAfterScan="true"
            :beepSuccessFreq="currentAction === 'IN' ? 1200 : 700"
            :beepDurMs="150"
            @decoded="handleQrDecoded"
            @error="handleQrError"
            class="w-full h-full rounded overflow-hidden shadow-2xl ring-4 ring-branco/10 relative"
          >
            <!-- Slot Overlay: retículo alvo (aiming) opcional visual centralizado -->
            <div
              class="absolute inset-0 pointer-events-none flex items-center justify-center"
            >
              <div
                class="w-64 h-64 border-2 border-branco/50 rounded mix-blend-overlay"
              ></div>
            </div>
          </QRCodeLeserScann>
        </div>
      </div>
    </div>

    <!-- MODAL DE ENTRADA MANUAL (Fallback) -->
    <div
      v-if="showManualModal"
      class="fixed inset-0 z-[60] flex items-center justify-center bg-cinza-900/80 backdrop-blur-sm p-4"
    >
      <div
        class="bg-branco rounded shadow-2xl max-w-sm w-full p-6 text-center transform transition-all animate-fade-in-up"
      >
        <h2 class="text-2xl font-bold mb-2 text-azul-profundo">
          {{ $t("zeiterfassung.manualEntryTitle") || "Manuelle Eingabe" }}
        </h2>
        <p
          class="text-sm text-cinza-500 mb-6 font-semibold uppercase tracking-wider"
        >
          {{
            currentAction === "IN"
              ? $t("zeiterfassung.in") || "Einstempeln"
              : $t("zeiterfassung.out") || "Ausstempeln"
          }}
        </p>

        <div class="mb-5 text-left">
          <label class="block text-sm font-semibold text-cinza-700 mb-2">
            {{ $t("zeiterfassung.manualEntryPrompt") || "Mitarbeiter-ID" }}
          </label>
          <input
            v-model="manualMatricula"
            type="text"
            ref="manualInputRef"
            class="w-full border-2 border-borda-suave rounded p-3 uppercase text-xl font-mono tracking-widest focus:border-azul-profundo focus:ring focus:ring-azul-profundo/20 transition-all outline-none"
            placeholder="M000001-3"
            @input="onManualInput"
            @keyup.enter="submitManualEntry"
            maxlength="9"
            autocomplete="off"
            :disabled="isProcessing"
          />
          <p
            v-if="manualError"
            class="text-vermelho-profundo text-sm mt-2 flex items-center font-medium"
          >
            <svg
              class="h-4 w-4 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            {{ manualError }}
          </p>
        </div>

        <div class="flex justify-between space-x-3 mt-8">
          <AktionButton
            :label="$t('buttons.cancel') || 'Abbrechen'"
            type="hell"
            class="flex-1 py-3"
            @click="closeManualModal"
            :disabled="isProcessing"
          />
          <AktionButton
            :label="$t('buttons.confirm') || 'Bestätigen'"
            type="azul"
            class="flex-1 py-3"
            @click="submitManualEntry"
            :disabled="!isManualValid || isProcessing"
          />
        </div>
      </div>
    </div>

    <!-- MENSAGEM FLUENTE DE SUCESSO (Toast em Tela Cheia / Overlay) -->
    <transition name="toast">
      <div
        v-if="successToast"
        class="fixed inset-0 z-[100] pointer-events-none flex items-center justify-center p-4"
      >
        <div
          class="absolute inset-0 bg-cinza-900/40 backdrop-blur-sm shadow-2xl"
        ></div>
        <div
          class="relative bg-branco rounded p-8 sm:p-12 max-w-md w-full text-center shadow-2xl flex flex-col items-center"
          :class="{
            'border-b-8 border-verde-suave': successToast.action === 'IN',
            'border-b-8 border-amarelo-profundo': successToast.action === 'OUT',
          }"
        >
          <div
            class="w-24 h-24 rounded-full flex items-center justify-center mb-6 text-branco shadow-inner"
            :class="
              successToast.action === 'IN'
                ? 'bg-verde-suave'
                : 'bg-amarelo-profundo'
            "
          >
            <!-- Check Icon -->
            <svg
              class="w-12 h-12"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="3"
                d="M5 13l4 4L19 7"
              ></path>
            </svg>
          </div>

          <h3
            class="text-3xl font-black mb-2 text-texto-principal tracking-tight"
          >
            {{
              successToast.action === "IN"
                ? $t("zeiterfassung.successIn") || "Angemeldet"
                : $t("zeiterfassung.successOut") || "Abgemeldet"
            }}
          </h3>

          <p
            class="text-2xl font-mono text-texto-secundario mb-6 mt-4 bg-cinza-claro px-6 py-2 rounded border border-cinza-200"
          >
            {{ formatMatriculaDisplay(successToast.mitarbeiterId) }}
          </p>

          <p class="text-lg text-cinza-400 font-medium">
            {{ successToast.timestampLabel }}
          </p>
        </div>
      </div>
    </transition>

    <!-- MODAL DE ALERTA (substitui alert() nativo) -->
    <AlertModal
      ref="alertModalRef"
      :message="''"
      :show-cancel="false"
      confirm-text="OK"
    />
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import QRCodeLeserScann from "@/components/QRCodeLeserScann.vue";
import AktionButton from "@/components/AktionButton.vue";
import AlertModal from "@/components/AlertModal.vue";
import {
  calcDV,
  toDbFormat,
  fromDbFormat,
  isSupremeMatricula,
} from "@/services/MatrikelService";
import { db } from "@/firebaseConfig";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { Capacitor } from "@capacitor/core";
import { Haptics, NotificationType } from "@capacitor/haptics";

const { t } = useI18n();

// RELÓGIO E DATA
const currentTime = ref("00:00:00");
const currentDateStr = ref("");
let clockInterval = null;

function updateClock() {
  const now = new Date();
  currentTime.value = now.toLocaleTimeString("de-DE", { hour12: false });
  currentDateStr.value = now.toLocaleDateString("de-DE", {
    weekday: "long",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
}

// ESTADO DA REDE
const isOnline = ref(navigator.onLine);

function updateNetworkStatus() {
  isOnline.value = navigator.onLine;
}

// ESTADOS DA VIEW
// IDLE, SCANNING
const state = ref("IDLE");
const currentAction = ref(null); // 'IN' ou 'OUT'

// COMPONENTES
const qrScanner = ref(null);

// CONTROLE DE DUPLO DISPARO / THROTTLE LOCAL (30 sec)
// Map<mitarbeiterId, lastTimestampMillis>
const localThrottleCache = new Map();
const THROTTLE_MS = 30000;

// CONTROLES MANUAIS E MODAL
const showManualModal = ref(false);
const manualMatricula = ref("");
const manualError = ref("");
const isManualValid = ref(false);
const isProcessing = ref(false);
const manualInputRef = ref(null);

// CONTROLE DE SUCESSO (TOAST)
const successToast = ref(null);
let toastTimeout = null;

// REF DO MODAL DE ALERTA
const alertModalRef = ref(null);

// FEEDBACK HÁPTICO — Capacitor nativo com fallback web
const isNative = Capacitor.isNativePlatform();

/**
 * Vibra o dispositivo.
 * @param {"success"|"error"} type — sucesso (pulso longo) ou erro (duplo curto)
 */
async function hapticFeedback(type) {
  try {
    if (isNative) {
      if (type === "success") {
        await Haptics.notification({ type: NotificationType.Success });
      } else {
        await Haptics.notification({ type: NotificationType.Error });
      }
    } else if (navigator.vibrate) {
      navigator.vibrate(type === "success" ? 200 : [100, 50, 100]);
    }
  } catch (_) {
    // Silencia erros em plataformas sem suporte
  }
}

// LIFECYCLE VUE
onMounted(() => {
  updateClock();
  clockInterval = setInterval(updateClock, 1000);
  window.addEventListener("online", updateNetworkStatus);
  window.addEventListener("offline", updateNetworkStatus);
});

onUnmounted(() => {
  if (clockInterval) clearInterval(clockInterval);
  if (toastTimeout) clearTimeout(toastTimeout);
  window.removeEventListener("online", updateNetworkStatus);
  window.removeEventListener("offline", updateNetworkStatus);
});

// ABRIR SCANNER
async function openScanner(action) {
  if (isProcessing.value) return;
  currentAction.value = action;
  state.value = "SCANNING";
  // O componente QRCodeLeserScann entra auto "active" pela prop vinculada a v-if logic,
  // mas esperamos nextTick pro componente existir.
  await nextTick();
}

// CANCELAR SCANNER -> ABRIR MODAL MANUAL
async function cancelScanner() {
  state.value = "IDLE";
  // Ao fechar SEM sucesso, abrimos o manual modal
  manualMatricula.value = "";
  manualError.value = "";
  isManualValid.value = false;
  showManualModal.value = true;
  await nextTick();
  if (manualInputRef.value) manualInputRef.value.focus();
}

// FECHAR MODAL MANUAL -> IDLE TOTAL
function closeManualModal() {
  showManualModal.value = false;
  currentAction.value = null;
  manualError.value = "";
}

// RETORNO DO QR CODE DECODED
async function handleQrDecoded(decodedText) {
  if (isProcessing.value) return;

  // Decodificado no QR pode vir como BD-Format ou Formato Exibição longo dependendo da etiqueta.
  // Vamos higienizar e extrair:
  const rawClean = (decodedText || "")
    .toString()
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();

  // Ex: "M0000013" -> letter: M, num6: 000001, dv: 3
  const letter = rawClean[0] || "";
  const digits = rawClean.slice(1).replace(/\D/g, "");
  const num6 = digits.slice(0, 6);
  const dv = digits.slice(6, 7);

  const dbTargetId = toDbFormat(letter, num6);
  const expectedDV = calcDV(letter, num6);

  // Validação dura:
  if (
    !dbTargetId ||
    isSupremeMatricula(dbTargetId) ||
    parseInt(dv, 10) !== expectedDV
  ) {
    // Alerta de QR inválido: como a camera para no pauseAfterScan=true,
    // soltamos um alert/toast de erro e resetamos a UI.
    // Feedback tátil: erro vibra diferente (duas vezes se suportado)
    hapticFeedback("error");
    if (alertModalRef.value) {
      alertModalRef.value.openModal({
        title: t("zeiterfassung.title"),
        message: t("zeiterfassung.errorInvalidId"),
      });
    }
    state.value = "IDLE";
    currentAction.value = null;
    return;
  }

  // É valido. Realizar registro DB Method=QR
  await performCheckIn(dbTargetId, "qr");
}

// ERROS DO PRÓPRIO SCANNER NA CAPTURA (ex: permissao negada)
function handleQrError() {
  // Ignora erros normais de "procurando qrcode" (o scanner da biblioteca html5 vomita err quando não acha no frame)
  // Somente logs críticos são jogados aqui
}

// HIGIENIZAÇÃO DO INPUT MANUAL
function onManualInput() {
  const s = String(manualMatricula.value || "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .toUpperCase();
  if (s.length > 0) manualMatricula.value = s[0] + s.slice(1);

  const letter = manualMatricula.value[0];
  const digits = manualMatricula.value.slice(1).replace(/\D/g, "");
  const num6 = digits.slice(0, 6);
  const dv = digits.slice(6, 7);

  let display = (letter || "") + num6;
  if (dv) display += "-" + dv;
  manualMatricula.value = display;

  manualError.value = "";
  isManualValid.value = false;

  if (letter && num6.length === 6 && dv.length === 1) {
    const dbFormat = toDbFormat(letter, num6);
    const expectedDV = calcDV(letter, num6);
    if (
      !dbFormat ||
      isSupremeMatricula(dbFormat) ||
      parseInt(dv, 10) !== expectedDV
    ) {
      manualError.value =
        t("zeiterfassung.errorInvalidId") || "Ungültige Mitarbeiter-ID.";
      return;
    }
    isManualValid.value = true;
  }
}

// SUBMIT MANUAL
async function submitManualEntry() {
  if (!isManualValid.value || isProcessing.value) return;
  const s = manualMatricula.value.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
  const letter = s[0];
  const num6 = s.slice(1, 7);
  const dbTargetId = toDbFormat(letter, num6);

  await performCheckIn(dbTargetId, "manual");
}

// REGISTRO CORE (Firebase Inserção Crúa e Throttle)
async function performCheckIn(mitarbeiterId, method) {
  isProcessing.value = true;

  try {
    const nowMs = Date.now();

    // Throttle / Trava (30 sec) para o MESMO funcionário (na mesma máquina)
    const lastCheckMs = localThrottleCache.get(mitarbeiterId) || 0;
    if (nowMs - lastCheckMs < THROTTLE_MS) {
      throw new Error("THROTTLE_BLOCK");
    }

    // Extrair data atual no fuso da Alemanha (Europe/Berlin) para "workDate"
    const hojeInGermany = new Date().toLocaleDateString("de-DE", {
      timeZone: "Europe/Berlin",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }); // Ex: "02.03.2026"

    // Converter "DD.MM.YYYY" para "YYYY-MM-DD"
    const [dd, mm, yyyy] = hojeInGermany.split(".");
    const safeWorkDate = `${yyyy}-${mm}-${dd}`;

    // Tudo certo para gravar.
    // Inserção atômica e imutável no zeiterfassung_raw (gera auto ID no firebase)
    const payload = {
      mitarbeiterId: mitarbeiterId,
      action: currentAction.value, // 'IN' ou 'OUT'
      method: method, // 'qr' ou 'manual'
      status: "valid", // Gestor invalida depois se precisar
      workDate: safeWorkDate, // Data base para os painéis gerenciais
      createdAt: serverTimestamp(), // Banco imutável contra frauds
    };

    await addDoc(collection(db, "zeiterfassung_raw"), payload);

    // Atualiza Cache Local
    localThrottleCache.set(mitarbeiterId, nowMs);

    // Disparar Som de finalização (Além do Beep da camera, validamos sucesso gravado)
    // Podemos colocar mais um beeps de confirmaçao se desejar ou apenas o layout
    // C. Feedback Háptico Sucesso
    hapticFeedback("success");

    triggerSuccessToast(mitarbeiterId, currentAction.value);
  } catch (err) {
    console.error("Zeiterfassung write error:", err);
    // C. Feedback háptico Erro
    hapticFeedback("error");

    if (err.message === "THROTTLE_BLOCK") {
      manualError.value = t("zeiterfassung.errorTooFast");
      // Se o modal manual não estava aberto, mostra AlertModal
      if (!showManualModal.value && alertModalRef.value) {
        alertModalRef.value.openModal({
          title: t("zeiterfassung.title"),
          message: t("zeiterfassung.errorTooFast"),
        });
      }
    } else {
      manualError.value = t("zeiterfassung.errorGeneric");
      if (!showManualModal.value && alertModalRef.value) {
        alertModalRef.value.openModal({
          title: t("zeiterfassung.title"),
          message: t("zeiterfassung.errorGeneric"),
        });
      }
    }
  } finally {
    isProcessing.value = false;
    // Resetar UI apenas se não for erro grave de bloqueio visual
    if (!manualError.value) {
      state.value = "IDLE";
      showManualModal.value = false;
      currentAction.value = null;
    }
  }
}

// EXIBIR TOAST 5 SEC E FECHAR
function triggerSuccessToast(mitarbeiterId, action) {
  const d = new Date();
  const timeLabel = d.toLocaleTimeString("de-DE", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const dateLabel = d.toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  successToast.value = {
    mitarbeiterId,
    action,
    timestampLabel: `${dateLabel} - ${timeLabel}`,
  };

  if (toastTimeout) clearTimeout(toastTimeout);
  toastTimeout = setTimeout(() => {
    successToast.value = null;
  }, 5000);
}

// DISPLAY HIGIENIZADO PARA TOAST (M000001-3)
function formatMatriculaDisplay(dbId) {
  const exibe = fromDbFormat(dbId);
  return exibe || dbId;
}
</script>

<style scoped>
/* Tamanhos dos ícones e labels: portrait padrão */
.zeit-icon {
  height: 15vh;
  width: 15vh;
}
.zeit-label {
  font-size: 1.75rem; /* ~text-3xl */
}
.zeit-divider {
  border-bottom: 2px solid theme("colors.branco / 20%");
}

/* ========== LANDSCAPE MOBILE ========== */
/* Aplica-se quando a tela está deitada E a altura é menor que 500px (celular) */
@media (orientation: landscape) and (max-height: 500px) {
  .zeit-main {
    flex-direction: row;
  }
  .zeit-icon {
    height: 12vh;
    width: 12vh;
  }
  .zeit-label {
    font-size: 1.25rem;
  }
  .zeit-divider {
    border-bottom: none;
    border-right: 2px solid theme("colors.branco / 20%");
  }
}

/* ========== DESKTOP / TABLET (>= 768px) ========== */
@media (min-width: 768px) {
  .zeit-icon {
    height: 14vh;
    width: 14vh;
  }
  .zeit-label {
    font-size: 2.25rem; /* ~text-4xl */
  }
}

/* Animação suave pro Modal e Toast */
.animate-fade-in-up {
  animation: fadeInUp 0.4s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

.toast-enter-active,
.toast-leave-active {
  transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}

.toast-enter-from {
  opacity: 0;
  transform: translateY(-50px) scale(0.9);
}

.toast-leave-to {
  opacity: 0;
  transform: translateY(50px) scale(0.9);
}
</style>
